import { useState, useEffect, Suspense } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard, Package, Users, ShoppingCart, Truck,
  Menu, X, ChevronLeft, ChevronRight, Tags, Clock, Shield, LogOut, Warehouse, FileText,
  Eye, EyeOff, Download, Share, PackagePlus, ScanLine,
} from 'lucide-react';
import { useHideFinancials } from '@/hooks/useHideFinancials';
import { fadeIn } from '@/lib/motion-constants';
import GlobalSearch from '@/components/GlobalSearch';
import { useTheme } from '@/contexts/ThemeContext';
import { ordersApi } from '@/lib/api';
import logo from "../assets/logo.png";
import PageSkeleton from '@/components/common/PageSkeleton';

const allNavItems = [
  { path: '/painel', label: 'Painel de Controlo', icon: LayoutDashboard, permission: 'view_dashboard' },
  { path: '/produtos', label: 'Produtos', icon: Package, permission: 'view_products' },
  { path: '/clientes', label: 'Clientes', icon: Users, permission: 'view_customers' },
  { path: '/encomendas', label: 'Encomendas', icon: ShoppingCart, permission: 'view_orders' },
  { path: '/orcamentos', label: 'Orçamentos', icon: FileText, permission: 'view_orders' },
  { path: '/compras', label: 'Compras', icon: PackagePlus, permission: 'manage_inventory' },
  { path: '/fornecedores', label: 'Fornecedores', icon: Truck, permission: 'view_suppliers' },
  { path: '/listas-precos', label: 'Listas de Preços', icon: Tags, permission: 'view_prices' },
  { path: '/atividade', label: 'Atividade Recente', icon: Clock, permission: 'view_activity' },
  { path: '/inventario', label: 'Inventário', icon: Warehouse, permission: 'manage_inventory' },
  { path: '/contagem', label: 'Contagem', icon: ScanLine, permission: 'manage_inventory' },
  { path: '/admin', label: 'Administração', icon: Shield, permission: 'manage_users' }
];

const BOTTOM_NAV = [
  { path: '/painel',     label: 'Painel',     icon: LayoutDashboard, permission: 'view_dashboard' },
  { path: '/clientes',   label: 'Clientes',   icon: Users,           permission: 'view_customers' },
  { path: '/encomendas', label: 'Encomendas', icon: ShoppingCart,    permission: 'view_orders' },
  { path: '/produtos',   label: 'Produtos',   icon: Package,         permission: 'view_products' },
  { path: '/inventario', label: 'Inventário', icon: Warehouse,       permission: 'manage_inventory' },
];

const BottomNav = ({ navItems, mobileOpen, setMobileOpen, pendingCount }) => {
  const location = useLocation();
  const { hasPermission } = useAuth();
  const items = BOTTOM_NAV.filter(i => !i.permission || hasPermission(i.permission));

  return (
    <nav className="bottom-nav">
      {items.map(item => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path || (item.path !== '/painel' && location.pathname.startsWith(item.path));
        const badge = item.path === '/encomendas' && pendingCount > 0 ? pendingCount : 0;
        return (
          <Link key={item.path} to={item.path} className={`bottom-nav-item ${isActive ? 'active' : ''}`}>
            <span className="relative">
              <Icon size={22} />
              {badge > 0 && (
                <span style={{
                  position: 'absolute', top: -5, right: -7,
                  minWidth: 16, height: 16, borderRadius: 8,
                  background: '#EF4444', color: '#fff',
                  fontSize: 10, fontWeight: 700, lineHeight: '16px',
                  textAlign: 'center', padding: '0 3px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </span>
            <span>{item.label}</span>
          </Link>
        );
      })}
      <button className={`bottom-nav-item ${mobileOpen ? 'active' : ''}`} onClick={() => setMobileOpen(o => !o)}>
        {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        <span>Mais</span>
      </button>
    </nav>
  );
};

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasPermission } = useAuth();
  useTheme(); // mantém contexto mas não usa toggle
  const [hideFinancials, toggleFinancials] = useHideFinancials();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (!hasPermission('view_orders')) return;
    const fetchPending = () =>
      ordersApi.getAll({ status: 'pending', limit: 1 })
        .then(r => setPendingCount(Array.isArray(r.data) ? r.data.length : (r.data?.total ?? 0)))
        .catch(() => {});
    fetchPending();
    const interval = setInterval(fetchPending, 60000);
    return () => clearInterval(interval);
  }, [hasPermission]);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (localStorage.getItem('pwa-install-dismissed')) return;
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
    if (isIOS) {
      setShowInstallBanner(true);
      return;
    }
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    setShowInstallBanner(false);
    setInstallPrompt(null);
  };

  const dismissInstallBanner = () => {
    setShowInstallBanner(false);
    localStorage.setItem('pwa-install-dismissed', '1');
  };

  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      const tablet = w >= 768 && w < 1024;
      setIsTablet(tablet);
      if (tablet) setCollapsed(true);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const navItems = allNavItems.filter(item => !item.permission || hasPermission(item.permission));

  const getCurrentPageTitle = () => {
    const item = navItems.find(item => location.pathname.startsWith(item.path));
    return item ? item.label : 'Ferramentas Pro';
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="app-container">
      {mobileOpen && !isTablet && (
        <div className="fixed inset-0 bg-black/50 z-30" onClick={() => setMobileOpen(false)} />
      )}

      <aside className={`sidebar ${collapsed ? 'sidebar-collapsed' : ''} ${mobileOpen ? 'open' : ''}`} data-testid="sidebar">
        <div className="sidebar-logo justify-center">
          <img src={logo} alt="Diavice" className={`object-contain transition-all scale-[2] ${collapsed ? 'h-12' : 'h-24'}`} />
        </div>

        <nav className="sidebar-nav" data-testid="sidebar-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || (item.path !== '/painel' && location.pathname.startsWith(item.path));
            return (
              <Link key={item.path} to={item.path}
                className={`nav-item ${isActive ? 'active' : ''}`}
                data-testid={`nav-${item.path.slice(1)}`}
                onClick={() => setMobileOpen(false)}>
                <Icon size={20} />
                {!collapsed && <motion.span {...fadeIn}>{item.label}</motion.span>}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t border-slate-800 space-y-2">
          {!collapsed && user && (
            <div className="px-2 py-1">
              <p className="text-sm text-slate-300 truncate">{user.name}</p>
              <p className="text-xs text-slate-500 truncate">{user.email}</p>
            </div>
          )}
          <button onClick={handleLogout}
            className="w-full flex items-center gap-2 p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-slate-800 transition-colors"
            data-testid="logout-btn">
            <LogOut size={18} />
            {!collapsed && <span className="text-sm">Sair</span>}
          </button>
          {!isTablet && (
            <button onClick={() => setCollapsed(!collapsed)}
              className="w-full flex items-center justify-center p-2 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors"
              data-testid="sidebar-toggle">
              {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            </button>
          )}
        </div>
      </aside>

      <main className={`main-content ${collapsed ? 'main-content-collapsed' : ''}`}>
        <header className="header">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <h1 className="text-base font-semibold text-slate-100 font-['Outfit'] md:text-xl shrink-0 hidden xs:block" data-testid="page-title">
                {getCurrentPageTitle()}
              </h1>
              <GlobalSearch />
            </div>
            {user && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={toggleFinancials}
                  className={`p-2 rounded-lg transition-colors ${hideFinancials ? 'text-amber-400 bg-amber-500/10 hover:bg-amber-500/20' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'}`}
                  title={hideFinancials ? 'Modo privado ativo — clica para mostrar custos' : 'Ocultar custos e margens'}>
                  {hideFinancials ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
                <span className="text-sm text-slate-400 hidden lg:inline">{user.name}</span>
                <div className="w-8 h-8 rounded-full bg-[#4A87D4] flex items-center justify-center text-white font-medium text-sm">
                  {user.name?.charAt(0)?.toUpperCase()}
                </div>
              </div>
            )}
          </div>
        </header>
        {showInstallBanner && (
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-blue-600/15 border-b border-blue-500/25">
            <div className="flex items-center gap-2.5 min-w-0">
              {installPrompt ? (
                <Download className="w-4 h-4 text-blue-400 flex-shrink-0" />
              ) : (
                <Share className="w-4 h-4 text-blue-400 flex-shrink-0" />
              )}
              <p className="text-sm text-slate-300 truncate">
                {installPrompt
                  ? 'Instala o Diavice ERP como app no tablet'
                  : 'Para instalar: toca em Partilhar → "Adicionar ao ecrã de início"'
                }
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {installPrompt && (
                <button
                  onClick={handleInstall}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white transition-colors"
                >
                  Instalar
                </button>
              )}
              <button onClick={dismissInstallBanner} className="p-1 text-slate-500 hover:text-slate-200 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        <div className="page-container">
          <Suspense fallback={<PageSkeleton />}>
            <Outlet />
          </Suspense>
        </div>
      </main>

      {/* Bottom nav — mobile only */}
      <BottomNav navItems={navItems} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} pendingCount={pendingCount} />
    </div>
  );
};

export default Layout;
